/**
 * Transmittals Shared Types
 * Platform-agnostic type definitions for transmittal hooks
 *
 * This file imports core types from the shared schema and defines
 * additional frontend-specific types for hooks and components.
 */

// ============================================================================
// Import Shared Types from Schema (Single Source of Truth)
// ============================================================================

import type {
  // API Request/Response Types
  SaveTransmittalsRequest,
  SaveTransmittalsResponse,
  SaveTransmittalsData,
  GetTransmittalDataByIdRequest,
  GetTransmittalDataByIdResponse,
  GetFilesAttachedToTransmittalRequest,
  GetFilesAttachedToTransmittalResponse,
  GetTransmittalFileVersionsForSpaceRequest,
  GetTransmittalFileVersionsForSpaceResponse,
  IsTransmittal403Request,
  IsTransmittal403Response,
  GetFormTemplatesForProjectRequest,
  GetFormTemplatesForProjectResponse,
  GetTransmittalDataRequest,
  // Component Types
  TransmittalFileSpaceAccess,
  TransmittalAccessSpace,
  TransmittalAccessUser,
  TransmittalFileMetadata as SharedTransmittalFileMetadata,
  TransmittalDetails,
  TransmittalFormTemplate,
  TransmittalRef,
} from "@ServerTypes/transmittals/transmittals.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

// Re-export shared types for convenience
export type {
  SaveTransmittalsRequest,
  SaveTransmittalsResponse,
  SaveTransmittalsData,
  GetTransmittalDataByIdRequest,
  GetTransmittalDataByIdResponse,
  GetFilesAttachedToTransmittalRequest,
  GetFilesAttachedToTransmittalResponse,
  GetTransmittalFileVersionsForSpaceRequest,
  GetTransmittalFileVersionsForSpaceResponse,
  IsTransmittal403Request,
  IsTransmittal403Response,
  GetFormTemplatesForProjectRequest,
  GetFormTemplatesForProjectResponse,
  GetTransmittalDataRequest,
  TransmittalFileSpaceAccess,
  TransmittalAccessSpace,
  TransmittalAccessUser,
  TransmittalDetails,
  TransmittalFormTemplate,
  TransmittalRef,
};

// ============================================================================
// Core Entity Types (Frontend-specific extensions)
// ============================================================================

export interface Space {
  _id: ObjectIdString;
  spaceName: string;
  description?: string;
  createdDate?: number;
  status?: string;
  id?: ObjectIdString;
  createdBy?: string;
  spaceId?: string;
  projectId?: string;
  createdByName?: string;
}

export interface OriginSpace {
  _id: string;
  spaceName: string;
}

export interface SelectedUser {
  id: string; // Composite key: `${userId}_${spaceId}`
  userId?: string;
  spaceId: string;
  spaceName: string;
  userName?: string;
  email?: string;
  organisation?: string;
  designation?: string;
}

export type SpaceWithUsers = TransmittalAccessSpace;

// ============================================================================
// Reference Types
// ============================================================================

export interface TransmittalReference {
  /** A file-version `_id`; sent as `attachedResources[].fileVersionId` / `forwardedResources[].resourceId`. */
  fileVersion: ObjectIdString;
  name: string;
  versionNumber: number;
  size: number;
  dateCreated: number;
  resourceType: string;
  fileType: string;
  remarks: string;
  /** A transmittal-version `_id`; sent as `forwardedResources[].transmittalId`. */
  transmittalVersionId?: ObjectIdString;
}

export interface ReferenceDatasetItem {
  id: string;
  name: string;
  versionNumber: number;
  size: number;
  dateCreated: number;
  resourceType: string;
  fileType: string;
  remarks: string;
}

// ============================================================================
// API Payload Types (derived from shared schema)
// ============================================================================

export type SpaceUserPayload = { spaceId: ObjectIdString; userId: ObjectIdString[] };

export interface AttachedResourcePayload {
  fileVersionId: ObjectIdString;
  remarks: string;
}

/** Transmittal save payload - matches SaveTransmittalsRequest from shared schema */
export type TransmittalPayload = SaveTransmittalsRequest;

// ============================================================================
// Response Types (derived from shared schema)
// ============================================================================

/** Transmittal data structure - matches GetTransmittalDataByIdResponse.data (shared schema) */
export type TransmittalData = TransmittalDetails;

/** File metadata from transmittal - uses shared type */
export type TransmittalFileMetadata = SharedTransmittalFileMetadata;

/** Metadata container for transmittal files - matches GetTransmittalDataByIdResponse.metadata */
export interface TransmittalMetadata {
  files?: TransmittalFileMetadata[] | undefined;
}

/** Attached file for display in UI */
export interface AttachedFile {
  id: string;
  /** Optional for the same reason as the fields below: `originalName` is optional on the file document. */
  name?: string | undefined;
  // Every optional here is filled from a server metadata field that is itself optional, so the
  // value really can arrive as an explicit `undefined` — the declarations say so.
  fileType?: string | undefined;
  version?: number | undefined;
  size?: number | undefined;
  addedOn?: number | undefined;
  resourceType?: string | undefined;
  isFolder: number;
  toSpaceData?: TransmittalFileSpaceAccess[] | undefined;
  accessDocId?: string | undefined;
  remarks?: string | null | undefined;
  hasNewerVersionInDestination?: boolean | undefined;
  latestVersionInDestination?:
    | {
        fileVersionInDestinationSpace?: number | undefined;
        fileVersionId?: string | undefined;
        transmittalId?: string | undefined;
        indexAtProjectLevel?: number | undefined;
      }
    | undefined;
}

// ============================================================================
// File Version Types
// ============================================================================

export interface FileVersionData {
  resourceVersionId: string;
  fileVersionNumber: number;
  createdDate: number;
  indexAtProjectLevel: number;
  transmittalId: string;
}

export interface FileVersionCheckResult {
  isLatest: boolean;
  currentVersion: FileVersionData | null;
  latestVersion: FileVersionData | null;
}

// ============================================================================
// Permission Types
// ============================================================================

/** Transmittal access response - matches IsTransmittal403Response */
export type TransmittalAccessResponse = IsTransmittal403Response;

export interface ModuleAccess {
  ready: boolean;
  /**
   * One of the numeric seat constants from `@Helpers/constants` (PORTAL_FULL_SEAT = 1 /
   * PORTAL_NO_SEAT = 0), as supplied by `useUserModuleAccess`, or `null` before the slices load.
   */
  portalSeat: number | null;
}

export interface ProjectSettings {
  transmittal?: {
    isVisible?: boolean;
    add?: {
      enable?: boolean;
    };
  };
}

// ============================================================================
// List Query Types
// ============================================================================

/**
 * The where clause `getTransmittalData` accepts, taken from the request schema rather than restated:
 * the server rejects any field outside `TRANSMITTAL_LIST_FILTERABLE_FIELDS` at the Zod boundary, so a
 * local `unknown[]` claimed the client could send anything when it provably cannot.
 */
export type TransmittalWhereClause = NonNullable<NonNullable<GetTransmittalDataRequest["value"]>["where"]>;

export interface TransmittalListQuery {
  projectId?: string;
  portalId?: string;
  where?: TransmittalWhereClause;
}

/**
 * The OTHER filter shape a saved view can hold — a bag of per-column conditions written by the grid,
 * not by this feature. It stays open because `layoutView.data.filters` is portal-authored client state
 * with no server schema; `applyViewLayout` only ever counts its keys and hands it straight back.
 */
export interface FilterConditions {
  [key: string]: unknown;
}

export interface ViewLayout {
  data: {
    columns?: string[];
    /** Saved views hold either the grid's condition bag or a ready-made where clause. */
    filters?: FilterConditions | TransmittalWhereClause;
  };
}

// ============================================================================
// Form Template Types (uses shared types)
// ============================================================================

/** Form template - uses shared TransmittalFormTemplate type */
export type FormTemplate = TransmittalFormTemplate;

// ============================================================================
// Hook Parameter Types
// ============================================================================

export interface UseCreateTransmittalFormParams {
  projectId: string;
  selectedSpaceId?: string | null;
  selectedReferences?: TransmittalReference[] | null;
  /** Sent as `saveTransmittalsRequest.reviewData.reviewId`, so it carries that field's brand. */
  reviewId?: NonNullable<SaveTransmittalsRequest["reviewData"]>["reviewId"] | null;
  mobile?: boolean;
  api: {
    fetchSpaces: (projectId: string) => Promise<Space[]>;
    fetchFormTemplate: (projectId: string, resourceType: string) => Promise<FormTemplate | null>;
    saveTransmittal: (data: TransmittalPayload) => Promise<{ data: SaveTransmittalsResponse }>;
  };
  callbacks?: {
    /** Receives the response's inner `data` (the new transmittal's id + counter), not the envelope. */
    onSuccess?: (response: SaveTransmittalsData) => void;
    onError?: (error: string) => void;
  };
}

export interface UseTransmittalDetailParams {
  transmittalVersionId: string;
  mobile?: boolean;
  api: {
    fetchTransmittal: (id: string) => Promise<void>;
    /** Called with `TransmittalData["id"]`, i.e. the transmittal's `objectIdString` `_id`. */
    checkAccess: (transmittalId: TransmittalData["id"]) => Promise<void>;
  };
}

export interface UseTransmittalListParams {
  projectId?: string;
  portalId?: string;
  byPortal?: boolean;
  mobile?: boolean;
}

export interface UseTransmittalPermissionsParams {
  projectSettings?: ProjectSettings | null;
  moduleAccess: ModuleAccess;
  byPortal?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

export const RESOURCE_TYPE_FILE = "RESOURCE_TYPE_FILE";
export const RESOURCE_TYPE_MERGED_FILE = "RESOURCE_TYPE_MERGED_FILE";
export const RESOURCE_TYPE_FOLDER = "RESOURCE_TYPE_FOLDER";
